import type { HoverAction, ActionResult } from '../types/actions';
import { isElementVisible, scrollIntoView } from '../utils/dom-utils';
import { waitForElement } from '../utils/wait-utils';

export async function executeHoverAction(action: HoverAction): Promise<ActionResult> {
  const startTime = Date.now();

  try {
    // Find the element
    let element = document.querySelector<HTMLElement>(action.selector);

    // Wait for element if specified
    if (!element && action.waitForElement) {
      element = await waitForElement<HTMLElement>(
        action.selector,
        action.waitForElement
      );
    }

    if (!element) {
      return {
        success: false,
        action,
        error: `Element not found: ${action.selector}`,
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: false
      };
    }

    // Check if visible
    if (!isElementVisible(element)) {
      return {
        success: false,
        action,
        error: 'Element is not visible',
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: true
      };
    }

    // Scroll into view if needed
    if (action.scrollIntoView !== false) {
      scrollIntoView(element);
    }

    // Dispatch mouse events to simulate hover
    const mouseEnterEvent = new MouseEvent('mouseenter', {
      bubbles: true,
      cancelable: true,
      view: window
    });
    element.dispatchEvent(mouseEnterEvent);

    const mouseOverEvent = new MouseEvent('mouseover', {
      bubbles: true,
      cancelable: true,
      view: window
    });
    element.dispatchEvent(mouseOverEvent);

    return {
      success: true,
      action,
      timestamp: Date.now(),
      duration: Date.now() - startTime,
      elementFound: true
    };
  } catch (error) {
    return {
      success: false,
      action,
      error: error instanceof Error ? error.message : String(error),
      timestamp: Date.now(),
      duration: Date.now() - startTime
    };
  }
}
